// EU external purchase links in the iOS app (native plugin EUExternalPurchasePlugin): which tier of
// purchase path a user gets, from the App Store storefront and their birth year.
//
// Apple's region rules for Apple Account ages set, per EU country, the minimum age for these links:
// below it, no purchase path at all; from it through 17, the link only behind a parental gate; 18 and
// up, the link directly. Keyed by ISO 3166-1 alpha-3 code, which is what AppStoreRegion's
// getStorefront() reports. The keys must stay the 27 countries in App.entitlements
// (custom-purchase-link.allowed-regions); euPurchase.test.js checks that.
export const EU_AGE_THRESHOLDS = {
  // 13
  BEL: 13, DNK: 13, EST: 13, FIN: 13, LVA: 13, MLT: 13, PRT: 13, SWE: 13,
  // 14
  AUT: 14, BGR: 14, CYP: 14, ITA: 14, LTU: 14, ESP: 14,
  // 15
  CZE: 15, FRA: 15, GRC: 15, SVN: 15,
  // 16
  HRV: 16, DEU: 16, HUN: 16, IRL: 16, LUX: 16, NLD: 16, POL: 16, ROU: 16, SVK: 16,
};

export const ADULT_AGE = 18;

export function isEUStorefront(countryCode) {
  return typeof countryCode === "string" && Object.prototype.hasOwnProperty.call(EU_AGE_THRESHOLDS, countryCode.toUpperCase());
}

// Age from the birth year alone (only the year is collected), rounded down: the age the user has
// before this year's birthday, currentYear - birthYear - 1. This gates minors, so without the exact
// birthday it assumes the younger age: a wrongly blocked adult waits a few months for the link, a
// wrongly admitted minor would skip the gate. null when the birth year is missing or implausible, so
// the caller never guesses.
export function ageFromBirthYear(birthYear, currentYear) {
  if (!Number.isInteger(birthYear) || birthYear < 1900 || birthYear > currentYear) return null;
  return Math.max(0, currentYear - birthYear - 1);
}

// "none" (no purchase path: the region fallback), "gated" (behind the parental gate) or "direct".
// An unknown country or an unknown age is "none": no payment path for a user whose age we don't know.
export function euPurchaseTier(countryCode, birthYear, currentYear) {
  if (!isEUStorefront(countryCode)) return "none";
  const age = ageFromBirthYear(birthYear, currentYear);
  if (age === null) return "none";
  const threshold = EU_AGE_THRESHOLDS[countryCode.toUpperCase()];
  if (age < threshold) return "none";
  if (age < ADULT_AGE) return "gated";
  return "direct";
}

// The plans screen's status for an iOS user on a storefront whose device Apple reports eligible:
// "eu" (direct), "eu_gated" (behind the parental gate), "ineligible" (no purchase path), or
// "eu_needs_birth_year": an EU storefront and no birth year on file, so the screen asks for it
// (birth year is optional at signup; App Review 5.1.1(v)) and then decides with euPurchaseTier,
// unchanged. Only a missing year asks: a stored year always goes straight to the tier.
export function euLinkStatusFor(countryCode, birthYear, currentYear) {
  if (!isEUStorefront(countryCode)) return "ineligible";
  if (birthYear === null || birthYear === undefined) return "eu_needs_birth_year";
  const tier = euPurchaseTier(countryCode, birthYear, currentYear);
  return tier === "direct" ? "eu" : tier === "gated" ? "eu_gated" : "ineligible";
}

// The parental gate's question: the sum of two numbers from 2 to 9. random is injectable for tests.
export function makeGateQuestion(random = Math.random) {
  const pick = () => 2 + Math.floor(random() * 8);
  const a = pick();
  const b = pick();
  return { a, b, answer: a + b };
}

// Whether the typed answer is right: digits only (surrounding spaces allowed), equal to the sum.
export function isGateAnswerCorrect(question, typed) {
  const text = String(typed ?? "").trim();
  return /^\d+$/.test(text) && Number(text) === question.answer;
}
